import React from "react";
import { Box, HStack, Text } from "@chakra-ui/react";
import { alpha, useCalTheme } from "./useCalTheme";

type Tone = "accent" | "success" | "warning" | "danger" | "neutral" | "purple";

/**
 * THE ONE enum → plain-words map for the calendars module (POLISH v1 rule 1). Grouped by what the value
 * describes, because the same token means different things in different enums: COMPANY is a calendar
 * TYPE ("Company calendar") and a VISIBILITY ("Whole company"). Unknown values show as-is, never hidden.
 */
export const CAL_WORDS = {
  type: {
    BUSINESS: "Business", COMPANY: "Company", CUSTOM: "Custom", EMAIL: "Email inbox", EMPLOYEE: "Staff member",
    PERSONAL: "Personal", PROJECT: "Project", RESOURCE: "Room or equipment", SHARED_EXTERNAL: "Shared with you",
  },
  visibility: {
    COMPANY: "Whole company", PRIVATE: "Only you", PUBLIC: "Public", SHARED: "Shared", TEAM: "Your team",
    CONFIDENTIAL: "Confidential", TRANSPARENT: "Shows as free",
  },
  status: {
    CANCELLED: "Cancelled", COMPLETED: "Done", CONFIRMED: "Confirmed", DRAFT: "Draft", TENTATIVE: "Maybe", PENDING: "Waiting",
  },
  rsvp: {
    ACCEPTED: "Going", DECLINED: "Not going", TENTATIVE: "Maybe", NEEDS_ACTION: "No reply yet", PENDING: "No reply yet", DELEGATED: "Passed on",
  },
  goal: {
    AT_RISK: "At risk", CANCELLED: "Dropped", COMPLETED: "Done", IN_PROGRESS: "Under way", NOT_STARTED: "Not started", ON_HOLD: "On hold",
  },
  broadcast: {
    SENT: "Sent", IN_PROGRESS: "Sending", FAILED: "Failed", PENDING: "Scheduled", SCHEDULED: "Scheduled",
  },
  share: { VIEW: "Can view", EDIT: "Can edit" },
  /** What's New roadmap item status (moduleConfig `roadmap[].status`). */
  roadmap: { completed: "Done", "in-progress": "Being built", planned: "Planned" },
  /** Public booking payment (`X-PAYMENT-STATUS` on a booked event). */
  payment: { NOT_REQUIRED: "No payment needed", PENDING: "Payment waiting", COMPLETED: "Paid", FAILED: "Payment failed", REFUNDED: "Refunded" },
  /** Where a bookable event type happens (`locationType`: Virtual / Physical / Phone). */
  location: { Virtual: "Online", Physical: "In person", Phone: "Phone call" },
  /** iCalendar METHOD on an imported invite (`iCalMethod`: REQUEST / CANCEL / REPLY …). */
  itip: {
    REQUEST: "Invitation", CANCEL: "Cancelled by the organiser", REPLY: "Reply", COUNTER: "New time suggested",
    DECLINECOUNTER: "New time turned down", PUBLISH: "Shared event", REFRESH: "Asked for the latest copy", ADD: "Extra dates added",
  },
  /** Goal category (`CalendarGoal.category`). */
  category: {
    MARKETING: "Marketing", GROWTH: "Growth", OPERATIONS: "Operations", TEAM: "Team", FINANCE: "Finance",
    PRODUCT: "Product", CUSTOMER: "Customer", PERSONAL: "Personal", OTHER: "Other",
  },
} as const;

const TONES: Record<keyof typeof CAL_WORDS, Record<string, Tone>> = {
  category: {},
  itip: { REQUEST: "accent", CANCEL: "danger", REPLY: "neutral", COUNTER: "warning", DECLINECOUNTER: "neutral", PUBLISH: "accent" },
  type: { BUSINESS: "accent", COMPANY: "purple", EMAIL: "accent", PERSONAL: "success", SHARED_EXTERNAL: "warning" },
  visibility: { PUBLIC: "success", PRIVATE: "neutral", SHARED: "accent", COMPANY: "purple", TEAM: "purple" },
  status: { CANCELLED: "danger", COMPLETED: "success", CONFIRMED: "success", DRAFT: "neutral", TENTATIVE: "warning", PENDING: "warning" },
  rsvp: { ACCEPTED: "success", DECLINED: "danger", TENTATIVE: "warning", NEEDS_ACTION: "neutral", PENDING: "neutral" },
  goal: { AT_RISK: "danger", CANCELLED: "neutral", COMPLETED: "success", IN_PROGRESS: "accent", NOT_STARTED: "neutral", ON_HOLD: "warning" },
  broadcast: { SENT: "success", IN_PROGRESS: "accent", FAILED: "danger", PENDING: "warning", SCHEDULED: "warning" },
  share: { VIEW: "accent", EDIT: "purple" },
  roadmap: { completed: "success", "in-progress": "accent", planned: "neutral" },
  payment: { NOT_REQUIRED: "neutral", PENDING: "warning", COMPLETED: "success", FAILED: "danger", REFUNDED: "purple" },
  location: { Virtual: "accent", Physical: "purple", Phone: "success" },
};

export type CalWordKind = keyof typeof CAL_WORDS;

/** Plain words for an enum value — the same map the pill uses, for places that need text not a pill. */
export function calWords(kind: CalWordKind, value?: string | null): string {
  if (!value) return "";
  return (CAL_WORDS[kind] as Record<string, string>)[value] ?? value;
}

/**
 * A status pill: coloured dot + plain words, 14px, never colour alone (POLISH v1 rule 3).
 * Pass `kind` + `status` to use the module map, or `label`/`tone` to override for a one-off.
 */
export function CalStatusPill({ kind = "status", status, label, tone: toneOverride }: { kind?: CalWordKind; status: string; label?: string; tone?: Tone }) {
  const t = useCalTheme();
  const tone = toneOverride ?? TONES[kind]?.[status] ?? "neutral";
  const c = { accent: t.primary, success: t.success, warning: t.warning, danger: t.danger, neutral: t.neutral, purple: t.purple }[tone];
  return (
    <HStack as="span" spacing={1.5} px={2.5} py={0.5} borderRadius="full" bg={alpha(c, 0.12)} borderWidth="1px" borderColor={alpha(c, 0.3)} display="inline-flex" flexShrink={0}>
      <Box as="span" w="8px" h="8px" borderRadius="full" bg={c} />
      <Text as="span" fontSize="sm" fontWeight="semibold" color={t.colorMode === "dark" ? c : t.text} whiteSpace="nowrap">{label ?? calWords(kind, status)}</Text>
    </HStack>
  );
}
